/**
 * `AW48` · Borde HTTP de la **configuración del chatbot** (`R1-F2`).
 *
 * Es el ÚNICO archivo de `configuracion-del-chatbot/` que emite `fetch`, igual
 * que `borde-plantillas.ts` lo es de `plantillas/` y `borde-exportacion.ts` de
 * `exportacion/`. La pantalla consume esto y no sabe de rutas ni de códigos de
 * estado.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐⭐ ① DEL REPO ENTRAN SÓLO TIPOS — Y LOS VALORES SE ANOTAN CONTRA ELLOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El alias `@contracts/*` vive en `tsconfig.json` y **NO** en `vite.config.ts`,
 * a propósito: `esbuild` borra los `import type` antes de resolver nada, así que
 * **importar un VALOR de un contrato rompe el build** (y no el typecheck — el
 * que lo rechaza es el bundler). Por eso los literales se repiten acá.
 *
 * ⛔ **Repetirlos SUELTOS no cumple.** El patrón que se copia es el de
 * `plantillas/borde-plantillas.ts` —`ESTADO_APROBADA_EN_META: TemplateApprovedStatus
 * = "APPROVED"`, el literal TIPADO—, **no** el de `borde-escritura.ts`, cuyo
 * comentario justificativo dice *«`contracts/admin-api` no fija rutas»* y **está
 * vencido**: hoy las fija, en `contracts/admin-api/http.ts` ⟦config-rutas⟧.
 *
 * ⭐ **Y acá el literal tipado llega más lejos que en el precedente, porque el
 * tope es un NÚMERO.** `TOPE_AL_PARTICIPANTE: typeof TOPE_DE_TEXTO_AL_PARTICIPANTE`
 * lo ancla al `4096` del contrato: escrito suelto, un `4069` no rompe nada y deja
 * la pantalla rechazando mensajes que la plataforma de mensajería acepta —o al
 * revés, dejando pasar uno que se corta—. Con la anotación, cualquier otro número
 * es `error TS2322`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔ ② NO HAY BRAZO EN NINGUNA DE LAS ~~SEIS~~ **SIETE** RUTAS, Y ES UNA DECISIÓN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `R1-F2 §5.1`: *«Un mismo texto rige para los dos brazos. Esto no es una
 * simplificación: es una protección del ensayo. Si los textos fueran por brazo,
 * alguien podría dejar los dos brazos con mensajes de contención distintos, y eso
 * es una asimetría de tratamiento que contamina la comparación. Con textos por
 * instancia, esa posibilidad no existe — no hay que validarla ni auditarla.»*
 *
 * ⚠️ **Es una divergencia deliberada con la superficie hermana**: el catálogo de
 * plantillas SÍ lleva el brazo en la ruta, y su motivo está escrito en el mismo
 * contrato (⟦el-brazo-si-va-en-el-cable⟧). Los dos criterios están escritos y
 * quien lea los dos archivos tiene que poder leer por qué son dos.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ ③ EL ORDEN VIAJA EN LA SECUENCIA, Y REORDENAR ES **UNA** OPERACIÓN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ninguna categoría lleva su lugar como campo. El contrato lo decide en
 * ⟦el-orden-es-de-la-SECUENCIA⟧ y el motivo es que un campo de lugar **admite dos
 * filas con el mismo número, admite huecos, y admite que el arreglo venga en un
 * orden distinto del que sus propios números dicen** — tres estados que ninguna
 * fuente define y que la pantalla tendría que resolver adivinando.
 *
 * ⛔ **Y mover una categoría es UN pedido, no N ediciones.** Con un `PUT` por
 * categoría, mover una dos lugares escribe tres o cuatro filas de auditoría para
 * UNA acción del operador, y la bitácora pasa a decir que hubo cuatro cambios
 * donde hubo uno.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ④ LO QUE ESTE BORDE NO HACE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   · **No valida.** Las reglas de `§5.4` y `§5.6` viven en `reglas.ts`, que es
 *     código puro y se puede ejercer sin DOM y sin red. Este archivo transporta.
 *   · **No pone el actor.** Lo resuelve `apps/admin-web` de la sesión. ⚠️ Y hoy
 *     esa sesión devuelve **un actor fijo que no identifica a nadie**: el registro
 *     da cronología, no atribución, y eso está declarado del lado que lo escribe.
 *   · **No escribe la fila de auditoría** ni sabe que existe.
 */

import type {
  CategoriaDeGuardrail,
  CategoriaId,
  ConfiguracionDelChatbot,
  NuevaCategoriaDeGuardrail,
  TextosDelChatbot,
  TextosOpcionalesDelChatbot,
  TipoDeGuardrail,
  TOPE_DE_TEXTO_AL_PARTICIPANTE,
} from "@contracts/admin-api/index.ts"
import type {
  ChatbotGuardrailPath,
  ChatbotGuardrailsOrdenPath,
  ChatbotGuardrailsPath,
  ChatbotPath,
  ChatbotTextosPath,
  ChatbotTextosRequestBody,
} from "@contracts/admin-api/http.ts"
/**
 * ⭐ `R2-UI7` · **La séptima ruta NO es de `contracts/admin-api`, y eso es la
 * decisión de `R2-CPL1`**: la bitácora es de `platform`, así que su cable —la
 * ruta, los dos query params, los códigos y las dos tablas de vocabulario— vive
 * en `contracts/platform/http.ts` (⟦el-vocabulario-va-con-el-cable-que-lo-lee⟧).
 * Por eso `r2-ui7.md:8-15` **sacó `R2-CAA1` del `needs`**.
 *
 * ⚠️ `AuditReadPath` es un alias de TIPO y entra como los otros cinco. Las dos
 * TABLAS (`AUDIT_SECTIONS`, `AUDIT_ACTION_LABELS`) son objetos de VALOR y **no
 * pueden entrar acá**: las ancla `pruebas/r2ui7-anclajes-de-auditoria.test.ts`
 * (`ADR-0356`).
 */
import type { AuditPageRow, AuditReadPath } from "@contracts/platform/http.ts"
import type { ConfiguracionInstancia } from "../configuracion.ts"
import { mensajeDeError } from "../mensaje-de-error.ts"

/**
 * ⭐⭐ **El tope de los textos que un participante LEE, anclado al contrato.**
 *
 * `R1-F2 §5.6`: *«Tope de 4.096 caracteres, con contador a la vista, solo en los
 * textos que se le envían al participante … Es el límite de un mensaje de texto
 * de la plataforma de mensajería: un mensaje que no entra es un mensaje que no
 * llega.»*
 *
 * La anotación `: typeof TOPE_DE_TEXTO_AL_PARTICIPANTE` es lo único que sostiene
 * este número. Ver el bloque ① del encabezado.
 *
 * ⛔ **No cubre el perfil de personalidad.** Ver `reglas.ts`.
 */
export const TOPE_AL_PARTICIPANTE: typeof TOPE_DE_TEXTO_AL_PARTICIPANTE = 4096

/**
 * Los dos tipos de categoría, para el selector de `§4 Sección 2`.
 *
 * Mismo criterio que el tope: el contrato los declara como valor
 * (`TIPOS_DE_GUARDRAIL`) y esta aplicación no puede importarlo, así que se
 * repiten **anotados con `TipoDeGuardrail`**. El día que un literal cambie allá,
 * este build se cae en vez de dibujar una opción que el borde rechaza.
 *
 * Van sin tilde y en minúscula: el concepto del dominio lleva la tilde, el
 * literal que viaja por el cable no.
 */
export const TIPOS_DE_CATEGORIA: readonly TipoDeGuardrail[] = ["contencion", "moderacion"]

/** El rótulo que ve el operador para cada literal. El literal no se muestra crudo. */
export const ROTULO_DEL_TIPO: Readonly<Record<TipoDeGuardrail, string>> = {
  contencion: "Contención",
  moderacion: "Moderación",
}

/**
 * ⭐⭐ **QUÉ HACE CADA TIPO, TEXTUAL DEL PRD.** `[18/9/2026]`
 *
 * `PRD_R1.1.md:669` fija las dos con estas palabras, como parte del campo Tipo del
 * panel de categoría: *«Tipo (Contención: “Mensaje + línea de ayuda. Siempre marca
 * el caso para revisión.” / Moderación: “Solo el mensaje. La conversación
 * sigue.”)»*. El boceto «Config. chatbot · Nueva / editar categoría» las dibuja al
 * lado de cada opción.
 *
 * ⛔ **No estaban en ningún lado del producto** —medido el 18/9—: el campo era un
 * `<select>` pelado con dos opciones y ninguna explicación. Y es el campo que
 * decide **si un menor que dice que se quiere lastimar recibe una línea de ayuda**:
 * elegir mal acá no tiene síntoma visible, el chatbot simplemente deja de contener.
 *
 * ⚠️ **Son distintas de la leyenda de la LISTA** —«CONTENCIÓN responde con mensaje
 * + línea de ayuda y siempre marca el caso para revisión.»—, que explica lo mismo
 * en otro lugar y con otras palabras. Las dos las fija una fuente distinta
 * (`CA-43`/`CA-44` la leyenda, `:669` éstas), así que **no se unifican**: unificarlas
 * haría que tocar una moviera la otra sin que ninguna fuente lo pidiera.
 */
export const DESCRIPCION_DEL_TIPO: Readonly<Record<TipoDeGuardrail, string>> = {
  contencion: "Mensaje + línea de ayuda. Siempre marca el caso para revisión.",
  moderacion: "Solo el mensaje. La conversación sigue.",
}

/*
 * El acuerdo de cable, todo junto y en un solo lugar. Los valores repiten los
 * literales del contrato; los TIPOS de path son del contrato, así que una
 * divergencia rompe el typecheck y no pasa desapercibida.
 *
 * ⚠️ El `{id}` va percent-encoded: el identificador de una categoría es opaco y
 * del almacén, y ningún contrato fija su forma.
 *
 * ⚠️ Y del otro lado hay un orden de resolución que no es opcional:
 * `guardrails/orden` es LITERAL y `guardrails/{id}` es parametrizado, así que el
 * literal se resuelve ANTES. Invertido, un reordenamiento cae en la rama de la
 * edición con un id que no existe y contesta 404 sin que nadie haya tocado el
 * reordenamiento. Eso es de `apps/admin-web`; se deja dicho porque acá se ve.
 */
const RUTA_CHATBOT: ChatbotPath = "chatbot"
const RUTA_TEXTOS: ChatbotTextosPath = `${RUTA_CHATBOT}/textos`
const RUTA_GUARDRAILS: ChatbotGuardrailsPath = `${RUTA_CHATBOT}/guardrails`
const RUTA_ORDEN: ChatbotGuardrailsOrdenPath = `${RUTA_GUARDRAILS}/orden`

function rutaDeLaCategoria(id: CategoriaId): ChatbotGuardrailPath {
  return `${RUTA_GUARDRAILS}/${encodeURIComponent(id)}`
}

/**
 * ⭐ `R2-UI7` · **La séptima: `GET auditoria`.** `contracts/platform/http.ts`
 * ⟦la-ruta⟧, `ADR-0354`.
 *
 * ⛔ **No cuelga de `chatbot/`.** Las seis primeras son la configuración del
 * chatbot; ésta es la bitácora de la INSTANCIA, que la pestaña Auditoría muestra
 * porque el PRD la puso en esta pantalla (`PRD_R2:2228`), no porque sea del
 * chatbot. Colgarla de `chatbot/auditoria` le mentiría al que lea la URL sobre
 * de qué es la fila.
 *
 * ⛔ **No lleva `arm` y no es un olvido**: ⟦ni-arm-ni-actor-en-el-cable⟧ lo
 * prohíbe en la ruta Y en la query, y la pestaña muestra LOS DOS brazos
 * (Regla 26).
 */
const RUTA_AUDITORIA: AuditReadPath = "auditoria"

/**
 * ⛔⛔ **Los dos query params, repetidos a mano y SIN alias de tipo que los ancle
 * — y ésa es la mitad frágil de este archivo.**
 *
 * `AUDIT_PAGE_PARAM` y `AUDIT_PAGE_SIZE_PARAM` (`contracts/platform/http.ts:231`
 * y `:234`) son VALORES y el contrato **no exporta un `typeof` de ninguno de los
 * dos**, así que acá no hay nada que hacer con un `import type`: un `"pagina"`
 * escrito en su lugar compila perfecto y le pide al servidor una query que
 * `leerConsultaDeAuditoria` rechaza con `400` — o sea, la pestaña se ve rota y
 * `tsc` está en cero.
 *
 * ⇒ Los ancla `pruebas/r2ui7-anclajes-de-auditoria.test.ts` por igualdad contra
 * el valor del contrato, junto con las dos tablas. `ADR-0356`.
 *
 * ⚠️ Y el contrato avisa que **el repo deletrea esto de dos maneras**: el cable
 * de envíos usa `pagina`/`tamano`. No se unifica acá (⟦la-query⟧).
 */
const PARAM_PAGINA = "page"
const PARAM_TAMANO_DE_PAGINA = "pageSize"

/**
 * ⭐ **La fila como la dibuja la pestaña: los CINCO campos que se muestran, y
 * ninguno más — anclados a `AuditPageRow` con `Pick`.**
 *
 * `Pick` y no una interfaz escrita de nuevo: el día que el contrato renombre
 * `actorName` o `occurredAt`, esto **no compila**, que es exactamente lo que una
 * copia a mano no daría.
 *
 * ⛔ **`arm`, `entityId`, `previousValue` y `origin` NO están, y es una decisión
 * con motivo.**
 *
 * ⭐ **ENMIENDA `[30/9/2026 · R3-UI10 · hallazgo R3-H-002]`: ~~`newValue`~~ SALIÓ de esta lista y
 * ahora SÍ está.** El motivo de abajo —que un lector que exigiera `arm` tendría que validar el
 * literal del brazo desde la SPA— **era sobre `arm`, y nunca fue sobre `newValue`**. Cuando esta
 * decisión se escribió (R2-UI7) no había ninguna frase-molde, así que recortarlo no costaba nada.
 * R3-UI7 agregó los huecos (`AUDIT_ACTION_HOLE_KEYS`) y el relleno ENCIMA de este borde, y **nadie
 * midió el empalme**: la Auditoría mostraba «…en el lote «[lote]».» con el dato en la base.
 * `newValue` es el único de los cinco que la pantalla MUESTRA, así que el argumento de «un campo
 * que nadie muestra» dejó de aplicarle. La tabla tiene CUATRO columnas (`PRD_R2:2234-2239`)
 * y la pestaña no filtra ni agrupa por brazo (`PRD_R2:2284`, Regla 26). Un
 * lector que exigiera `arm` para construir la fila tendría que validar el
 * literal del brazo **desde la SPA**, que es el import de valor que
 * `vite.config.ts:10-16` prohíbe (`ADR-0330`) — o sea, un campo que nadie
 * muestra obligando a la pantalla a copiar una lista de brazos.
 *
 * ⚠️ **AMBIGÜEDAD ANOTADA, sin decidir** (`r2-ui7.md` RT-11 vs. `goal`): RT-11
 * dice *«`Cambio` de `action` con `previous_value` y `new_value`»* y el `goal` de
 * la misma etapa dice *«Cambio por `AUDIT_ACTION_LABELS[action]` con fallback al
 * `action` crudo»*, sin los dos valores. Las dos lecturas: ① la frase es sólo del
 * `action` (lo que dice el `goal`, y lo que `AUDIT_ACTION_LABELS` puede dar: es
 * un `Record<action, frase>` y no conoce los valores); ② la celda muestra además
 * el «de X a Y». Se implementó ①, que es la que el `goal` fija y la única que el
 * contrato puede sostener hoy; ② exigiría un formato de `unknown → texto` que
 * ninguna fuente escribe y que ⟦el-transporte-es-opaco⟧ dice que no es del cable.
 */
export type FilaDeAuditoria = Pick<
  AuditPageRow,
  /**
   * ⭐ `newValue` ENTRA `[30/9/2026 · R3-UI10 · hallazgo R3-H-002 · CP-R3-F5-059]`.
   *
   * No es un campo más: es el que RELLENA los huecos de las frases-molde
   * (`AUDIT_ACTION_HOLE_KEYS`, `contracts/platform/http.ts`). Sin él, la Auditoría mostraba el
   * marcador crudo —«…en el lote «[lote]».»— aunque el dato estaba en la base.
   *
   * ⛔ **Y va en el `Pick`, y no se lee con un cast, para que `tsc` pueda verlo.** `auditoria.tsx`
   * lo leía con `fila as FilaDeAuditoria & { newValue?: unknown }`, así que el compilador no tenía
   * cómo saber que ese campo era SIEMPRE `undefined`: el cast hacía compilar un hueco. Es la misma
   * especie que `ADR-0401` (`reason: string` libre en `contracts/participants`), y la lección es la
   * misma: un hueco que sólo un caso de QA puede ver va a volver.
   */
  "newValue" | "actor" | "actorName" | "action" | "entity" | "occurredAt"
>

/** Una página de la bitácora, ya leída: las filas y el total de LOS DOS brazos. */
export interface PaginaDeAuditoria {
  readonly filas: readonly FilaDeAuditoria[]
  /**
   * ⛔ **El total de la BASE, nunca `filas.length`** — y acá es el de los dos
   * brazos sumados (`AuditPageResponseBody.total`). Sin él, el paginado no sabe
   * cuántas páginas hay.
   */
  readonly total: number
}

/**
 * El borde visto desde el navegador.
 *
 * ⛔⛔ **RASTRO `[22/9/2026]` · `R2-UI7` · `ADR-0356`.** Acá decía, textual:
 *
 *   ~~*«**SEIS operaciones y ninguna más**, que son las seis que
 *   `ChatbotConfigAdminApi` declara»*~~
 *
 * **La mitad que venció es el «y ninguna más», no la cuenta.** Son SIETE: las
 * seis de `ChatbotConfigAdminApi` más `auditoria()`, que **no es de ese
 * contrato** —es `GET auditoria` de `contracts/platform/http.ts`— y por eso la
 * frase vieja no la podía nombrar. La pestaña Auditoría vive en esta pantalla
 * (`PRD_R2:2228`) y su borde es éste: un séptimo borde «porque es de otro
 * contrato» dejaría a la pantalla con dos objetos inyectados donde el resto de
 * la consola tiene uno.
 *
 * No hay un `publicarConfiguracion()` ni un `guardarBorrador()`: `§5.2` dice que
 * **quien guarda, publica**, así que un segundo verbo sería un paso intermedio
 * que el PRD dice que no existe.
 */
export type BordeConfiguracion = {
  /** `GET chatbot`. Los nueve textos y las categorías **en su orden**. */
  readonly configuracion: () => Promise<ConfiguracionDelChatbot>
  /**
   * `PUT chatbot/textos`. **Los nueve, completos, siempre.**
   *
   * No hay parcial: `§5.4` los hace obligatorios a todos, así que un `PATCH`
   * permitiría expresar el estado —ocho cargados y uno vacío— que el PRD dice
   * que no puede existir. Contesta `204`: que quedó guardado **se observa
   * leyendo**, no devolviendo lo que se acaba de mandar.
   *
   * ⭐⭐ **RASTRO `[28/9/2026]` · `R3-UI2` · B16 — LOS «NUEVE» DE ARRIBA SON UNA
   * CUENTA VENCIDA, Y SE MIDIÓ.** El renglón dice *«Los nueve, completos,
   * siempre»* y hoy `TextosDelChatbot` declara **DIEZ** campos (desde que `R2`
   * sumó `botonSinRespuesta`) **más** la clave de la encuesta, que es de
   * `TextosOpcionalesDelChatbot`. El cuerpo del cable es
   * `ChatbotTextosRequestBody = TextosDelChatbot & TextosOpcionalesDelChatbot`
   * (`contracts/admin-api/http.ts:1183`): **once claves, planas**. Lo que sigue
   * valiendo del renglón viejo es el «completos, siempre» — que es lo que lo hacía
   * valioso.
   *
   * ⛔ **La encuesta viaja SIEMPRE, también cuando se edita otro mensaje.** Su
   * ausencia no es «vacía»: es cuerpo malformado y `400`
   * (`R3-AW2`). Por eso entra en la firma y no como un opcional que un llamador
   * viejo pueda omitir — omitirla borraría la encuesta en silencio.
   */
  readonly guardarTextos: (
    textos: TextosDelChatbot,
    opcionales: TextosOpcionalesDelChatbot,
  ) => Promise<void>
  /** `POST chatbot/guardrails`. Devuelve la categoría **con el `id` que le asignó el almacén**. */
  readonly crearCategoria: (nueva: NuevaCategoriaDeGuardrail) => Promise<CategoriaDeGuardrail>
  /**
   * `PUT chatbot/guardrails/{id}`. **Reemplaza la categoría entera, incluido el
   * tipo.**
   *
   * Cambiar el tipo cambia qué campos hay —una contención que pasa a moderación
   * pierde su línea de ayuda y gana su marca—, que es exactamente el cambio que
   * un parcial deja a medio hacer.
   */
  readonly editarCategoria: (
    id: CategoriaId,
    cambio: NuevaCategoriaDeGuardrail,
  ) => Promise<CategoriaDeGuardrail>
  /**
   * `DELETE chatbot/guardrails/{id}`. **Devuelve la categoría COMPLETA que
   * borró**, y no un `204` pelado.
   *
   * `Plan_Releases.md:404`: *«sacar una categoría de guardrail sin querer no rompe
   * nada visible, simplemente el chatbot deja de contener»*. Con un `204` lo único
   * que la operación produce es el id, y guardar sólo el id deja de ser una
   * tentación para pasar a ser el camino natural.
   */
  readonly borrarCategoria: (id: CategoriaId) => Promise<CategoriaDeGuardrail>
  /**
   * `PUT chatbot/guardrails/orden` con **todos** los ids en el orden deseado.
   * Devuelve la lista en el orden nuevo. Ver el bloque ③ del encabezado.
   */
  readonly reordenarCategorias: (
    orden: readonly CategoriaId[],
  ) => Promise<readonly CategoriaDeGuardrail[]>
  /**
   * ⭐ `R2-UI7` · `GET auditoria?page={n}&pageSize={m}`. **Los dos brazos, del
   * más reciente al más antiguo.**
   *
   * ⛔ **`pagina` es BASE 1** (⟦la-query⟧) y **los dos parámetros son
   * OBLIGATORIOS**: `apps/admin-web` no les pone default a propósito
   * (`rutas.ts:2138-2142`, *«la pantalla ya tomó la primera opinión sobre cuál
   * es la página inicial, y una segunda acá divergiría sin que nada avise»*), así
   * que mandar uno solo contesta `400`, no la página 1.
   *
   * ⛔ **La pestaña NO reordena lo que llega.** El orden lo fija `readPage` y lo
   * une `apps/admin-web/src/auditoria.ts`; reordenar del lado del cliente sería
   * reordenar UNA ventana, que no arregla nada y crea una segunda verdad sobre
   * el orden (⟦el-orden-lo-fija-readpage⟧).
   */
  readonly auditoria: (pagina: number, tamanoDePagina: number) => Promise<PaginaDeAuditoria>
}

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === "object" && valor !== null && !Array.isArray(valor)
}

function cadena(valor: unknown): string | null {
  return typeof valor === "string" ? valor : null
}

function listaDeCadenas(valor: unknown): readonly string[] | null {
  if (!Array.isArray(valor)) return null
  const salida: string[] = []
  for (const crudo of valor) {
    if (typeof crudo !== "string") return null
    salida.push(crudo)
  }
  return salida
}

/**
 * Una categoría del cable.
 *
 * ⛔⛔ **Se DISCRIMINA por `tipo` antes de leer los campos, y ésa es la mitad que
 * se implementa mal.** Un lector que leyera los seis campos siempre aceptaría las
 * combinaciones que el tipo del contrato existe para prohibir: una moderación con
 * línea de ayuda entra, se dibuja, y el chatbot termina mandándole a un menor un
 * recurso de crisis por decir una grosería.
 *
 * ⭐ El `mensaje` de una contención puede ser `null` —y **sólo ahí**—: significa
 * «usá el de la instancia». En moderación es obligatorio y no hay genérico del
 * que tirar.
 */
function leerCategoria(valor: unknown): CategoriaDeGuardrail | null {
  if (!esObjeto(valor)) return null

  const id = cadena(valor["id"])
  if (id === null) return null
  const nombre = cadena(valor["nombre"])
  if (nombre === null) return null
  const temas = listaDeCadenas(valor["temas"])
  if (temas === null) return null

  const tipo = valor["tipo"]

  if (tipo === "contencion") {
    const crudoMensaje = valor["mensaje"]
    if (crudoMensaje !== null && typeof crudoMensaje !== "string") return null
    const lineaDeAyuda = cadena(valor["lineaDeAyuda"])
    if (lineaDeAyuda === null) return null
    return { id, tipo, nombre, temas, mensaje: crudoMensaje, lineaDeAyuda }
  }

  if (tipo === "moderacion") {
    const mensaje = cadena(valor["mensaje"])
    if (mensaje === null) return null
    const marcarParaRevision = valor["marcarParaRevision"]
    if (typeof marcarParaRevision !== "boolean") return null
    return { id, tipo, nombre, temas, mensaje, marcarParaRevision }
  }

  return null
}

/**
 * ⭐ Una fila de la bitácora tal como viaja (`EnElCable<AuditPageRow>`).
 *
 * ⛔⛔ **`occurredAt` llega como TEXTO y se pasa a `Date` ACÁ.** Un `Date` no
 * cruza JSON (⟦un-date-no-cruza-json⟧): del otro lado sale un ISO y TypeScript
 * **sigue creyendo que es un `Date`** si nadie lo desarma, así que
 * `formatearFecha(fila.occurredAt)` compilaría y reventaría en el navegador con
 * `fecha.getTime is not a function`. Se desarma en el borde, que es la punta que
 * habla JSON.
 *
 * ⛔ **`actorName` acepta `null` y NO se rellena.** `null` no es «no pude
 * resolverlo»: es *«ese correo no corresponde a ningún usuario»* (`RT-8`, y las
 * filas de máquina de la ingesta son el caso real). Un `?? ""` acá dejaría una
 * celda en blanco donde iba una persona; la pestaña muestra el `actor` crudo.
 *
 * ⚠️ Una fecha que no se entiende NO tira la fila: entra como `Date` inválido y
 * `formatearFecha` la dibuja como «fecha inválida». **La fila nunca se esconde**
 * (`RT-11`), y una bitácora que se calla lo que no reconoce no es una bitácora.
 */
function leerFilaDeAuditoria(valor: unknown): FilaDeAuditoria | null {
  if (!esObjeto(valor)) return null

  const actor = cadena(valor["actor"])
  if (actor === null) return null
  const action = cadena(valor["action"])
  if (action === null) return null
  const entity = cadena(valor["entity"])
  if (entity === null) return null
  const occurredAt = cadena(valor["occurredAt"])
  if (occurredAt === null) return null

  const crudoNombre = valor["actorName"]
  if (crudoNombre !== null && typeof crudoNombre !== "string") return null

  /*
   * `newValue` viaja TAL CUAL: el contrato lo declara `unknown` y el relleno de los huecos ya
   * valida su forma antes de usarlo (`auditoria.tsx`, `esObjeto`). Una fila SIN el campo sigue
   * siendo una fila válida —«una fila sin el dato»— y no tumba el parseo de la página.
   */
  return {
    actor,
    action,
    entity,
    actorName: crudoNombre,
    newValue: valor["newValue"],
    occurredAt: new Date(occurredAt),
  }
}

/**
 * La página entera del cable.
 *
 * ⛔ **`total` se exige y no se deriva de `rows.length`.** Sin él la pantalla no
 * sabe cuántas páginas hay, y la salida natural —contar las filas que llegaron—
 * dice «una página» sobre una bitácora de diez mil.
 */
function leerPaginaDeAuditoria(valor: unknown): PaginaDeAuditoria | null {
  if (!esObjeto(valor)) return null

  const total = valor["total"]
  if (typeof total !== "number" || !Number.isFinite(total)) return null

  const crudas = valor["rows"]
  if (!Array.isArray(crudas)) return null

  const filas: FilaDeAuditoria[] = []
  for (const cruda of crudas) {
    const fila = leerFilaDeAuditoria(cruda)
    if (fila === null) return null
    filas.push(fila)
  }

  return { filas, total }
}

function leerCategorias(valor: unknown): readonly CategoriaDeGuardrail[] | null {
  if (!Array.isArray(valor)) return null
  const salida: CategoriaDeGuardrail[] = []
  for (const cruda of valor) {
    const categoria = leerCategoria(cruda)
    if (categoria === null) return null
    salida.push(categoria)
  }
  return salida
}

/**
 * Los DIEZ textos del cable.
 *
 * ⭐⭐ **RASTRO · `R2-UI9` · 23/9/2026 — ERAN NUEVE.** `ADR-0082`: el renglón decía
 * «Los nueve textos del cable». `R2-CAA2` sumó `botonSinRespuesta` a
 * `TextosDelChatbot` como campo **requerido** (`contracts/admin-api/index.ts`
 * ⟦botonSinRespuesta⟧, `RT-11`), y este lector era uno de los tres sitios que `tsc`
 * dejaba rojos hasta esta etapa.
 *
 * ⛔ **Entra con la MISMA forma que los otros nueve: `cadena()`, y `null` corta la
 * lectura entera.** Un `?? ""` sólo para el nuevo —«total es texto nuevo y ninguna
 * instancia lo tiene cargado»— es exactamente el default escondido que el párrafo
 * de abajo descarta, y dejaría a la pantalla dibujando una fila vacía que el
 * operador nunca pidió.
 *
 * ⛔ **Ninguno se rellena si falta.** Un `?? ""` acá sería *«el texto de respaldo
 * escondido en el código»* que `§5.4` descarta con nombre: falta configuración,
 * nadie se queja, y el producto elige solo. Una respuesta a la que le falta un
 * campo es una respuesta que no se entiende, y se dice.
 */
function leerTextos(valor: unknown): TextosDelChatbot | null {
  if (!esObjeto(valor)) return null

  const personalityProfile = cadena(valor["personalityProfile"])
  const offTopicMessage = cadena(valor["offTopicMessage"])
  const mensajeSinDatoDuro = cadena(valor["mensajeSinDatoDuro"])
  const mensajeSinCoberturaEnElBanco = cadena(valor["mensajeSinCoberturaEnElBanco"])
  const fallbackText = cadena(valor["fallbackText"])
  const avisoDeDemora = cadena(valor["avisoDeDemora"])
  const pedidoDeMensajeDeTexto = cadena(valor["pedidoDeMensajeDeTexto"])
  const mensajeDeNoDerivacion = cadena(valor["mensajeDeNoDerivacion"])
  const mensajeQueRemiteAlBoton = cadena(valor["mensajeQueRemiteAlBoton"])
  const botonSinRespuesta = cadena(valor["botonSinRespuesta"])

  if (
    personalityProfile === null ||
    offTopicMessage === null ||
    mensajeSinDatoDuro === null ||
    mensajeSinCoberturaEnElBanco === null ||
    fallbackText === null ||
    avisoDeDemora === null ||
    pedidoDeMensajeDeTexto === null ||
    mensajeDeNoDerivacion === null ||
    mensajeQueRemiteAlBoton === null ||
    botonSinRespuesta === null
  ) {
    return null
  }

  return {
    personalityProfile,
    offTopicMessage,
    mensajeSinDatoDuro,
    mensajeSinCoberturaEnElBanco,
    fallbackText,
    avisoDeDemora,
    pedidoDeMensajeDeTexto,
    mensajeDeNoDerivacion,
    mensajeQueRemiteAlBoton,
    botonSinRespuesta,
  }
}

/**
 * ⭐ `R3-SAN-UI` · **Los textos OPCIONALES del cable.** `R3-CAA1` · B15.
 *
 * `ChatbotConfigResponseBody` **es** `ConfiguracionDelChatbot`
 * (`contracts/admin-api/http.ts` ⟦config-textos⟧): `textosOpcionales` viaja, así
 * que acá se LEE — no se rellena. Es la opción que la Regla 3 de
 * `specs/R3-0-saneamiento/spec.md` pide primero: *«leer del cable lo que el
 * contrato ya declara»*.
 *
 * ⛔ **La clave ausente es CUERPO MALFORMADO, nunca «vacía», y el contrato lo
 * dice con el modo de falla:** *«un `encuestaDeSatisfaccion?: string` volvería
 * indistinguibles “el operador la dejó vacía” de “el cliente no la mandó”»*, con
 * *«una SPA vieja borrando la encuesta en silencio»* como consecuencia. Por eso
 * entra con la MISMA forma que los diez de `leerTextos` —`cadena()`, y `null`
 * corta la lectura entera hacia el camino de error que la pantalla ya tiene— y
 * **no** con un `?? ""`.
 *
 * ⚠️ `""` SÍ es una respuesta válida del cable —es el estado inicial de la
 * encuesta, que nadie siembra—: lo que se rechaza es la clave que no está o que
 * no es una cadena.
 *
 * ⛔ **Dibujarla es de `R3-UI2`** (la octava fila de «Mensajes del motor»). Esta
 * etapa sólo la deja entrar.
 */
function leerTextosOpcionales(valor: unknown): TextosOpcionalesDelChatbot | null {
  if (!esObjeto(valor)) return null
  const encuestaDeSatisfaccion = cadena(valor["encuestaDeSatisfaccion"])
  if (encuestaDeSatisfaccion === null) return null
  return { encuestaDeSatisfaccion }
}

function leerConfiguracion(valor: unknown): ConfiguracionDelChatbot | null {
  if (!esObjeto(valor)) return null
  const textos = leerTextos(valor["textos"])
  if (textos === null) return null
  const textosOpcionales = leerTextosOpcionales(valor["textosOpcionales"])
  if (textosOpcionales === null) return null
  const categorias = leerCategorias(valor["categorias"])
  if (categorias === null) return null
  return { textos, textosOpcionales, categorias }
}

/**
 * Arma el borde contra la `admin-web` configurada.
 *
 * Función y no módulo con estado, por lo mismo que los otros bordes: la dirección
 * llega en tiempo de ejecución (`configuracion.json`) y no hay ningún momento de
 * import en el que conocerla.
 */
export function crearBordeConfiguracionHttp(config: ConfiguracionInstancia): BordeConfiguracion {
  const raiz = config.adminWebUrl

  /**
   * `credentials: "include"` en los ~~seis~~ **siete** pedidos (`R2-UI7` sumó
   * `GET auditoria`, y la sesión de esa ruta es la MISMA cookie: la transversal
   * entera es 403 para el Acompañante, `CA-F6-38`) y ningún `Authorization` armado
   * acá, igual que en los otros bordes: la sesión la maneja `admin-web` en una
   * cookie `HttpOnly` que el navegador envía solo. Un token que el JavaScript
   * tuviera que leer para ponerlo en un header sería un token que la pantalla
   * tuvo que GUARDAR, y esta unidad no guarda nada.
   */
  const pedir = async (ruta: string, init: RequestInit): Promise<Response> => {
    let respuesta: Response
    try {
      respuesta = await fetch(`${raiz}/${ruta}`, { ...init, credentials: "include" })
    } catch (causa) {
      throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${raiz}).`, 0), {
        cause: causa,
      })
    }
    if (!respuesta.ok) {
      throw errorDelBorde(
        await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
        respuesta.status,
      )
    }
    return respuesta
  }

  const conJson = (cuerpo: unknown): RequestInit => ({
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(cuerpo),
  })

  const unaCategoria = async (respuesta: Response): Promise<CategoriaDeGuardrail> => {
    const categoria = leerCategoria(await respuesta.json())
    if (categoria === null) {
      throw errorDelBorde(
        "El servidor contestó una categoría de guardrail en una forma que no se entiende.",
        502,
      )
    }
    return categoria
  }

  return {
    async configuracion(): Promise<ConfiguracionDelChatbot> {
      const ruta: ChatbotPath = RUTA_CHATBOT
      const respuesta = await pedir(ruta, {
        method: "GET",
        headers: { Accept: "application/json" },
      })
      const configuracion = leerConfiguracion(await respuesta.json())
      if (configuracion === null) {
        throw errorDelBorde(
          "El servidor contestó la configuración del chatbot en una forma que no se entiende.",
          502,
        )
      }
      return configuracion
    },

    async guardarTextos(
      textos: TextosDelChatbot,
      opcionales: TextosOpcionalesDelChatbot,
    ): Promise<void> {
      const ruta: ChatbotTextosPath = RUTA_TEXTOS
      /**
       * ⭐⭐ `R3-UI2` · B16 · **EL CUERPO LLEVA SIEMPRE LA CLAVE DE LA ENCUESTA.**
       *
       * `ChatbotTextosRequestBody` es `TextosDelChatbot & TextosOpcionalesDelChatbot`
       * (`contracts/admin-api/http.ts:1183`): una sola tapa plana con las once
       * claves. Anotado con ese tipo **a propósito** —el patrón del literal
       * tipado del bloque ① del encabezado—: una clave que falte deja de
       * compilar de este lado en vez de volver como un `400` del servidor.
       *
       * ⛔ **El `.trim()` es SÓLO de la encuesta.** `PRD_R3.md:1772`: *«Un texto
       * de «Encuesta de satisfacción» con solo espacios se guarda vacío»*.
       * Recortar también los diez obligatorios cambiaría en silencio lo que el
       * operador escribió, y ninguna fuente lo pide para ellos.
       */
      const cuerpo: ChatbotTextosRequestBody = {
        ...textos,
        encuestaDeSatisfaccion: opcionales.encuestaDeSatisfaccion.trim(),
      }
      // Contesta `204` sin cuerpo: no se lee `.json()`.
      await pedir(ruta, { method: "PUT", ...conJson(cuerpo) })
    },

    async crearCategoria(nueva: NuevaCategoriaDeGuardrail): Promise<CategoriaDeGuardrail> {
      const ruta: ChatbotGuardrailsPath = RUTA_GUARDRAILS
      return unaCategoria(await pedir(ruta, { method: "POST", ...conJson(nueva) }))
    },

    async editarCategoria(
      id: CategoriaId,
      cambio: NuevaCategoriaDeGuardrail,
    ): Promise<CategoriaDeGuardrail> {
      // El id va SÓLO en la ruta. En el cuerpo abriría la pregunta de qué pasa
      // cuando los dos no coinciden, y ninguna fuente la contesta.
      const ruta: ChatbotGuardrailPath = rutaDeLaCategoria(id)
      return unaCategoria(await pedir(ruta, { method: "PUT", ...conJson(cambio) }))
    },

    async borrarCategoria(id: CategoriaId): Promise<CategoriaDeGuardrail> {
      const ruta: ChatbotGuardrailPath = rutaDeLaCategoria(id)
      return unaCategoria(
        await pedir(ruta, { method: "DELETE", headers: { Accept: "application/json" } }),
      )
    },

    async reordenarCategorias(
      orden: readonly CategoriaId[],
    ): Promise<readonly CategoriaDeGuardrail[]> {
      const ruta: ChatbotGuardrailsOrdenPath = RUTA_ORDEN
      // El cuerpo es un arreglo DESNUDO de ids, y son TODOS. Un movimiento
      // (`{ id, a: 3 }`) dejaría al borde decidiendo qué pasa con los demás.
      const respuesta = await pedir(ruta, { method: "PUT", ...conJson(orden) })
      const categorias = leerCategorias(await respuesta.json())
      if (categorias === null) {
        throw errorDelBorde(
          "El servidor contestó la lista de categorías en una forma que no se entiende.",
          502,
        )
      }
      return categorias
    },

    async auditoria(pagina: number, tamanoDePagina: number): Promise<PaginaDeAuditoria> {
      const ruta: AuditReadPath = RUTA_AUDITORIA
      /*
       * ⛔ La query se arma con `URLSearchParams` y no concatenando: los valores
       * van percent-encoded sin que haya que acordarse, y los NOMBRES son los del
       * contrato. Ver el bloque de `PARAM_PAGINA`: son lo único de esta ruta que
       * ningún tipo ancla.
       */
      const query = new URLSearchParams({
        [PARAM_PAGINA]: String(pagina),
        [PARAM_TAMANO_DE_PAGINA]: String(tamanoDePagina),
      })
      const respuesta = await pedir(`${ruta}?${query.toString()}`, {
        method: "GET",
        headers: { Accept: "application/json" },
      })
      const leida = leerPaginaDeAuditoria(await respuesta.json())
      if (leida === null) {
        throw errorDelBorde(
          "El servidor contestó la bitácora de auditoría en una forma que no se entiende.",
          502,
        )
      }
      return leida
    },
  }
}
